import type { StatusTone } from "@/components/status-badge";
import type { BranchMeta, HistSeries, HistSummary, MetricSeries, Verdict } from "../../types";

/** Helpers shared by the camera and pizza flow tabs (no components here). */

/** stacking order of the verdict bars: calm OK at the bottom, the stuck ones on top */
export const VERDICT_ORDER: Verdict[] = ["ok", "nok", "invalid_photo", "no_pizza", "pending", "missing"];

/** badge tone of a verdict: OK calm, photo problems amber, NOK / stuck red */
export const VERDICT_TONE: Record<Verdict, StatusTone> = {
  ok: "ok",
  nok: "nok",
  invalid_photo: "warn",
  no_pizza: "warn",
  pending: "neutral",
  missing: "nok",
};

/** share of invalid photos (or no pizza) from which a branch / camera is suspicious, and broken */
export const INVALID_WARN = 0.3;
export const INVALID_NOK = 0.5;

export type Tone = "neutral" | "warn" | "nok";

export const invalidTone = (share: number | null | undefined): Tone =>
  share === null || share === undefined ? "neutral" : share >= INVALID_NOK ? "nok" : share >= INVALID_WARN ? "warn" : "neutral";

/** branch lookup by BiteBerry branch id */
export function branchLookup(meta: BranchMeta[] | undefined) {
  const byId = new Map((meta ?? []).map((branch) => [branch.id, branch]));
  return (id: string | null | undefined): BranchMeta | null => (id ? byId.get(id) ?? null : null);
}

/** counter of a metric per bucket (zeros when the backend never counted it) */
export const counterValues = (metrics: MetricSeries, name: string): number[] =>
  metrics.counters[name] ?? metrics.t.map(() => 0);

/** one statistic of a histogram metric per bucket (gaps when it was never observed) */
export const histValues = (metrics: MetricSeries, name: string, stat: keyof HistSeries): (number | null)[] =>
  metrics.hists[name]?.[stat] ?? metrics.t.map(() => null);

export const counterTotal = (metrics: MetricSeries, name: string): number => metrics.totals.counters[name] ?? 0;

export const histTotal = (metrics: MetricSeries, name: string): HistSummary | null => metrics.totals.hists[name] ?? null;

/** lower-case text without diacritics, so "karvina" finds "Karviná" */
export const searchText = (...parts: (string | null | undefined)[]): string =>
  parts
    .filter(Boolean)
    .join(" ")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export const hasValues = (values: (number | null | undefined)[] | undefined) =>
  !!values?.some((v) => typeof v === "number" && v !== 0);
